import { createSlice } from "@reduxjs/toolkit";
import { API } from "../../../utils/axios/index";
import { requestStart, requestComplete, requestFail } from "../global/global";
import { setErrorMessage } from "../../../utils/helpers/utility_helpers";
import { startCase } from "lodash";

// API endpoints
// GET - /users - Get all users
// PUT - /users/{user_id} - Update user role and plan groups
// DELETE - /users/{user_id}?updated_by={id} - Delete a user
// GET - /plan-groups/dropdown - Get plan groups dropdown
// GET - /roles/dropdown - Get roles dropdown
const initialState = {
  users: [],
  rolesList: [],
  planGroupsList: [],
  loading: false,
  error: null
};

export const planManagementSlice = createSlice({
  name: "planManagement",
  initialState,
  reducers: {
    fetchUsersStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchUsersSuccess: (state, action) => {
      state.users = action.payload;
      state.loading = false;
    },
    fetchUsersFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchRolesListSuccess: (state, action) => {
      state.rolesList = action.payload;
      state.loading = false;
    },
    fetchPlanGroupsListSuccess: (state, action) => {
      state.planGroupsList = action.payload;
      state.loading = false;
    },
    updateUserSuccess: (state, action) => {
      const updatedUser = action.payload;
      state.users = state.users.map(user =>
        user.user_id === updatedUser.user_id ? updatedUser : user
      );
    },
    deleteUserSuccess: (state, action) => {
      const deletedUserId = action.payload;
      state.users = state.users.filter(user => user.user_id !== deletedUserId);
    }
  }
});

// Export actions
export const {
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFail,
  fetchRolesListSuccess,
  fetchPlanGroupsListSuccess,
  updateUserSuccess,
  deleteUserSuccess
} = planManagementSlice.actions;

// Thunk actions
export const fetchUsers = () => async (dispatch) => {
  try {
    dispatch(requestStart());
    dispatch(fetchUsersStart());

    try {
      // Make the API call
      const response = await API.get('/users');
      const list = [...(response?.data?.data || [])].sort((a, b) => a.username.localeCompare(b.username));
      dispatch(fetchUsersSuccess(list));
      dispatch(requestComplete());
    } catch (apiError) {
      console.error('API call failed:', apiError);
      const errorMessage = setErrorMessage(apiError);
      dispatch(fetchUsersFail(errorMessage));
      dispatch(requestFail(errorMessage));
    }
  } catch (error) {
    console.error("Error fetching users:", error);
    const errorMessage = setErrorMessage(error);
    dispatch(fetchUsersFail(errorMessage));
    dispatch(requestFail(errorMessage));
  }
};

export const fetchRolesList = () => async (dispatch) => {
  try {
    dispatch(requestStart());

    try {
      // Make the API call
      const response = await API.get('/roles/dropdown');

      // Handle nested data structure if present
      const rolesData = (response?.data?.data || response?.data || []).map(item => ({
        value: item.value,
        label: startCase(item.label)
      }));

      dispatch(fetchRolesListSuccess(rolesData));
      dispatch(requestComplete());
    } catch (apiError) {
      console.error('API call failed:', apiError);
      const errorMessage = setErrorMessage(apiError);
      dispatch(requestFail(errorMessage));
    }
  } catch (error) {
    console.error("Error fetching roles:", error);
    const errorMessage = setErrorMessage(error);
    dispatch(requestFail(errorMessage));
  }
};

export const fetchPlanGroupsList = () => async (dispatch) => {
  try {
    dispatch(requestStart());

    try {
      // Make the API call
      const response = await API.get('/plan-groups/dropdown');

      // Handle nested data structure if present
      const planGroupsData = response?.data?.data || response?.data || [];

      dispatch(fetchPlanGroupsListSuccess(planGroupsData));
      dispatch(requestComplete());
    } catch (apiError) {
      console.error('API call failed:', apiError);
      const errorMessage = setErrorMessage(apiError);
      dispatch(requestFail(errorMessage));
    }
  } catch (error) {
    console.error("Error fetching plan groups:", error);
    const errorMessage = setErrorMessage(error);
    dispatch(requestFail(errorMessage));
  }
};

export const updateUser = (userId, userData, updatedBy) => async (dispatch) => {
  try {
    dispatch(requestStart());

    const payload = {
      updated_by: updatedBy,
      fields_to_update: {
        role: userData.role,
        plan_group: userData.planGroup || []
      }
    };

    try {
      // Make the API call
      await API.put(`/users/${userId}`, payload);

      // Fetch updated users to ensure we have the latest data
      dispatch(fetchUsers());

      dispatch(requestComplete({
        success: true,
        successMessage: "User updated successfully.",
      }));
    } catch (apiError) {
      console.error("API call failed:", apiError);
      const errorMessage = setErrorMessage(apiError);
      dispatch(requestFail(errorMessage));
    }
  } catch (error) {
    console.error("Error updating user:", error);
    const errorMessage = setErrorMessage(error);
    dispatch(requestFail(errorMessage));
  }
};

export const deleteUser = (userId, updatedBy) => async (dispatch) => {
  try {
    dispatch(requestStart());

    try {
      // Make the API call
      await API.delete(`/users/${userId}?updated_by=${updatedBy}`);

      // Fetch updated users list after deletion
      dispatch(fetchUsers());

      dispatch(requestComplete({
        success: true,
        successMessage: "User deleted successfully.",
      }));
    } catch (apiError) {
      console.error("API call failed:", apiError);
      const errorMessage = setErrorMessage(apiError);
      dispatch(requestFail(errorMessage));
    }
  } catch (error) {
    console.error("Error deleting user:", error);
    const errorMessage = setErrorMessage(error);
    dispatch(requestFail(errorMessage));
  }
};

export default planManagementSlice.reducer;
